import { useCallback, useMemo } from 'react';
import { findIndex } from 'lodash';

import { AdminBereich, bereicheFuerPfad } from '@/constants/bereiche';
import useAuthStore from '@/store/auth';
import { Permissions, UserRole } from '@/types';
import { hasPermission } from '@/utils/permissions';

export type VisibleItem<T> = [
  permisssions: Permissions[],
  visibleItem: T,
  not?: Permissions[],
];

const usePermissions = () => {
  const { user } = useAuthStore();

  const roles = useMemo<UserRole[]>(() => user?.roles ?? [], [user]);

  // Unteradmin (Entscheid Alex 16.10.2026 20:42): ueberall, wo die Rolle `sa`
  // gilt, gilt `unteradmin` genauso — auch hier im FE. hasRole('sa') und
  // isSA() sind deshalb fuer beide wahr. Die drei Ausnahmen pruefen
  // ausdruecklich isEchterSA() (Rechteverwaltung, Rollenvergabe) bzw.
  // hatBereich() (ausgeschaltete Bereiche).
  const hasRole = useCallback(
    (role: UserRole) =>
      roles.includes(role) || (role === 'sa' && roles.includes('unteradmin')),
    [roles],
  );

  const isSA = useCallback(() => hasRole('sa'), [hasRole]);
  /** Nur die echte Rolle `sa` — fuer Rechteverwaltung und Rollenvergabe. */
  const isEchterSA = useCallback(() => roles.includes('sa'), [roles]);
  const isUnteradmin = useCallback(() => roles.includes('unteradmin'), [roles]);

  /**
   * Ist der Bereich fuer diesen Nutzer offen? Nur der Unteradmin hat
   * Schalter; fehlt `bereiche` (alte Session), ist fuer ihn alles zu.
   */
  const hatBereich = useCallback(
    (bereich: AdminBereich) =>
      !roles.includes('unteradmin') || (user?.bereiche ?? []).includes(bereich),
    [roles, user],
  );

  /** Darf der Nutzer diese /home-Seite oeffnen? (Detailseiten: ein Datenbereich genuegt) */
  const darfPfad = useCallback(
    (pathname: string) => {
      const bereiche = bereicheFuerPfad(pathname);
      return bereiche === null || bereiche.some((b) => hatBereich(b));
    },
    [hatBereich],
  );
  const isVP = useCallback(() => hasRole('vp'), [hasRole]);
  const isCC = useCallback(() => hasRole('cc'), [hasRole]);
  const isCallcenter = useCallback(() => isCC(), [isCC]);

  // Onboarding-Rolle (Vorgabe Alex 2026-07-19): nur Telefonie-Seite +
  // Buchungs-Wizard, aber self-buchungsfaehig wie ein VP.
  //
  // Bewusst Rollen-Identitaet statt Permission-String — analog isCC(). Das
  // FE kann `cc` und `telefonie` ueber Permissions gar nicht unterscheiden:
  // User::getAllowedPermissionsAttribute kuerzt per strtok auf das erste
  // Segment, beide Rollen liefern also identisch den Bucket `callcenter`.
  // Sobald Welle 2 (Decision #47) volle Permission-Strings ausliefert, kann
  // das hier auf can() umgestellt werden.
  const isTelefonie = useCallback(() => hasRole('telefonie'), [hasRole]);

  // Rollen ohne jede Navigation: Vollbild-Wizard ohne Sidebar/AppBar.
  // Zentral, damit ein neuer Chrome-Bestandteil nicht an einer der Stellen
  // vergessen wird.
  const isWizardOnlyRole = useCallback(
    () => isCC() || isTelefonie(),
    [isCC, isTelefonie],
  );

  // Manager-Sicht: nach K5 (Decision #48) ueber explizites is_leadership-
  // Bool aus UserResource/SignInResource. Backend setzt das Flag basierend
  // auf $user->can('dashboard.leadership.view'). SA + Ex-VV/VD/VM-User
  // (jetzt VP mit User-Level-Permission) sind Leadership.
  //
  // TODO Welle 2 (Berechtigungs-Architektur, Decision #47): generische
  // can()-Pruefung im FE, sobald User::getAllowedPermissionsAttribute
  // volle Permission-Strings + direkte User-Permissions liefert.
  const isLeadership = useCallback(
    () => user?.is_leadership === true,
    [user],
  );

  // Case-Uebergabe (Entscheid Alex 2026-07-22, erweitert durch Decision #239
  // am 21.10.2026): Sicht-Level "Team" oder "Alle" fuer Beratungsdaten. Die
  // Bedingung wird NICHT hier nachgebaut — das Backend liefert sie als
  // flachen Bool aus demselben Praedikat, das DealPolicy::changeOwner prueft
  // (VisibilityService::darfFremdeCasesUebergeben + SA-Rollen-Bypass). Damit koennen FE-Gate und
  // Server-Antwort nicht auseinanderlaufen.
  //
  // Bewusst getrennt von isLeadership(): die Manager-SICHT (Dashboard,
  // Berater-Filter) haengt weiter an dashboard.leadership.view, das
  // Uebergabe-RECHT nicht mehr. Fehlt das Feld (alte localStorage-Session),
  // ist das Gate zu — fail-closed.
  const canTransferCases = useCallback(
    () => user?.can_transfer_cases === true,
    [user],
  );

  // DSGVO-Loeschung (Decision #190): dasselbe Praedikat, das
  // CustomerPolicy::delete prueft, als flacher Bool. Fehlt das Feld (alte
  // localStorage-Session), ist das Gate zu — fail-closed, wie bei
  // canTransferCases.
  const canDeleteCustomers = useCallback(
    () => user?.can_delete_customers === true,
    [user],
  );

  // Antragserstellung (BER-67, Entscheid Alex 11.09.2026): dasselbe
  // Praedikat wie das Routen-Gate `permission:antraege.erstellen`, als
  // flacher Bool. Fehlt das Feld (alte localStorage-Session), ist das Gate
  // zu — fail-closed, wie canDeleteCustomers.
  const canCreateApplications = useCallback(
    () => user?.can_create_applications === true,
    [user],
  );

  // Termin-Umverteilung (Entscheid Alex 14./17.09.2026): dasselbe Praedikat
  // wie MeetingPolicy::umverteilen im Backend — Permission
  // `meetings.umverteilen` ODER Sichtebene `case` >= hierarchy —, geliefert
  // als flacher Bool (VisibilityService::darfTermineUmverteilen). Bewusst
  // NICHT ueber hasPermissions('meetings'): der Bucket steht bei jedem VP.
  // Fehlt das Feld (alte localStorage-Session), ist das Gate zu — fail-closed.
  const canRedistributeMeetings = useCallback(
    () => user?.can_redistribute_meetings === true,
    [user],
  );

  // „Case Übergabe" (Entscheid Alex 01.08. + 14.10.2026): jeder aktive VP für
  // EIGENE Cases, über beide Wege. Fremde Cases weiter nur über die beiden
  // Flags oben. Die Owner-Gleichheit ist der Teil der Policy, den das Backend
  // nicht als Nutzer-Flag liefern kann — sie haengt am Case, nicht am Nutzer.
  const istEigenerCase = useCallback(
    (ownerId?: number | null) =>
      user?.can_uebergeben_eigene_cases === true
      && ownerId != null
      && Number(ownerId) === Number(user?.id),
    [user],
  );

  // Knopf „Case Übergabe" am Case (Case-Transfer-Weg, DealPolicy::changeOwner).
  const darfCaseUebergeben = useCallback(
    (ownerId?: number | null) => canTransferCases() || istEigenerCase(ownerId),
    [canTransferCases, istEigenerCase],
  );

  // Knopf „Case Übergabe" am Termin (Umverteilen-Weg, MeetingPolicy::umverteilen).
  const darfTerminUebergeben = useCallback(
    (fallOwnerId?: number | null) => canRedistributeMeetings() || istEigenerCase(fallOwnerId),
    [canRedistributeMeetings, istEigenerCase],
  );

  // Mitarbeiter-Marker: nach K5 (Decision #48) ohne BO. Nur VP-User
  // (alle Ex-Mitarbeiter werden VPs konsolidiert).
  const isFieldWorker = useCallback(() => isVP(), [isVP]);

  // Legacy compatibility helpers
  const isSuperAdmin = useCallback(() => isSA(), [isSA]);
  const isAdmin = useCallback(() => isSA(), [isSA]);

  const hasPermissions = useCallback(
    (...permissions: Permissions[]) => {
      if (!user) return false;
      return permissions.every((p) => user.permissions.includes(p));
    },
    [user],
  );

  /**
   * Darf dieser Nutzer die Pipeline/Meetings nach BERATER filtern?
   *
   * 🔥 ZWEI BEDINGUNGEN, UND DIE ZWEITE WAR DER FEHLER (Befund Alex
   * 29.08.2026). Das Gate hiess bis dahin nur `isLeadership()` — und das
   * beantwortet eine ANDERE Frage: `VisibilityService::isLeadership()` ist
   * wahr, sobald die **User**-Sichtbarkeit mindestens `hierarchy` ist. Die
   * Berater-Liste kommt aber aus `GET /api/users`, und der Endpunkt verlangt
   * die Permission `users.viewAny`. Wer das eine hat und das andere nicht,
   * bekam beim Oeffnen der Pipeline einen 403 — die Seite lud, die Cases
   * waren da, und darueber stand „Keine Berechtigung".
   *
   * Auf bwlive am 29.08.2026 gemessen: **14 aktive Nutzer** in genau dieser
   * Lage, darunter Alex selbst. Bei **13 von ihnen** ist die CASE-Sicht `own`
   * — sie sehen ohnehin nur eigene Faelle, ein Berater-Filter ist fuer sie
   * gegenstandslos. Der 14. hat `hierarchy` und braucht die Permission; das
   * ist ein Rechte-Entscheid, kein Bugfix.
   *
   * Niemand verliert dadurch eine Funktion, die er heute hat: fuer alle 14
   * lief der Abruf schon bisher in den 403, die Liste blieb leer.
   *
   * 🔥 DIE ZWEITE HAELFTE KOMMT ALS FLAG, NICHT AUS `permissions` — und der
   * naheliegende Weg waere falsch gewesen. `hasPermissions('users')` sieht aus
   * wie die Pruefung, ist aber die Bucket-Frage: der Backend-Getter kuerzt per
   * `strtok` auf das erste Segment, `users.viewAny` und `users.view` landen
   * beide auf `users`. Der Filter erschiene also erneut genau denen, die in
   * den 403 laufen. `can_view_user_list` wertet das Backend mit demselben
   * Praedikat aus wie UserPolicy::viewAny — wie bei can_transfer_cases.
   *
   * 🔥 SEIT 20.10.2026 HAENGT DER FILTER AN DER CASE-SICHT (Entscheid Alex,
   * docs/tasks/pipeline-berater-filter-sicht.md). Die 29.08.-Fassung liess
   * ihn praktisch nur der SA: `users.viewAny` hat kein VP, und auf bwlive
   * sahen 14 VPs fremde Cases ohne jede Auswahl. Jetzt: Sicht
   * „Beratungsdaten" Team/Alle oder Admin, als Flag aus
   * `VisibilityService::darfCasesNachBeraterFiltern` — dasselbe Praedikat
   * wie `GET /api/deals/berater-auswahl`, aus dem die Liste kommt. Die
   * Bucket-Falle oben gilt unveraendert: auch dieses Recht ist aus
   * `permissions` nicht ablesbar.
   */
  const canFilterByBerater = useCallback(
    () => user?.can_filter_cases_by_berater === true,
    [user],
  );

  const canManageTeams = useCallback(
    () => hasPermissions('teams'),
    [hasPermissions],
  );
  const canViewAllContacts = useCallback(
    () => hasPermissions('contacts') && isSA(),
    [hasPermissions, isSA],
  );
  const canManageMeetings = useCallback(
    () => hasPermissions('meetings'),
    [hasPermissions],
  );
  const canViewReports = useCallback(
    () => hasPermissions('reports'),
    [hasPermissions],
  );
  const select = useCallback(
    <T>(components: VisibleItem<T>[]) => {
      if (!user) return null;

      const componentIndex = findIndex(components, (component) => {
        return hasPermission(component[0], user.permissions, component[2]);
      });

      return componentIndex === -1 ? null : components[componentIndex][1];
    },
    [user],
  );

  return {
    select,
    hasRole,
    isSA,
    isEchterSA,
    isUnteradmin,
    hatBereich,
    darfPfad,
    isVP,
    isCC,
    isCallcenter,
    isTelefonie,
    isWizardOnlyRole,
    isLeadership,
    canFilterByBerater,
    canTransferCases,
    canDeleteCustomers,
    canCreateApplications,
    canRedistributeMeetings,
    darfCaseUebergeben,
    darfTerminUebergeben,
    isFieldWorker,
    isSuperAdmin,
    isAdmin,
    hasPermissions,
    canManageTeams,
    canViewAllContacts,
    canManageMeetings,
    canViewReports,
  };
};

export default usePermissions;
